import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardContent,
  Grid,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Box,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import {
  format,
  parseISO,
  startOfHour,
  startOfDay,
  startOfISOWeek,
  startOfMonth,
  addHours,
  addDays,
  addWeeks,
  addMonths,
  isBefore,
} from "date-fns";
import { BlenderFlowChart } from "../Lamination1/charts/BlenderFlowChart";
import { FlowRateChart } from "../Lamination1/charts/FlowRateChart";
import { LoadingOverlay } from "../../common/LoadingOverlay";
import { MaterialTotalsCards } from "../../common/MaterialTotalsCards";
import { getBlenderFlow, type BlenderFlowRow } from "../../../api/blender";
import { nowAsNashvilleLocal, nashvilleLocalToISO } from "../../../utils/nashvilleTime";

type GroupedBar = {
  timestamp: number;
  label: string;
  flow1: number;
  flow2: number;
  flow3?: number;
};

type GroupedLine = {
  timestamp: number;
  label: string;
  flowRate1: number;
  flowRate2: number;
  flowRate3?: number;
};

// ---------- Date helpers ----------
function toValidDate(input: number | string | Date): Date | null {
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  if (typeof input === "number") {
    const ms = input > 1e12 ? input : input * 1000;
    const d = new Date(ms);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof input === "string") {
    let s = input.trim();
    if (!s.includes("T") && s.includes(" ")) s = s.replace(" ", "T");
    let d = new Date(s);
    if (!isNaN(d.getTime())) return d;
    try {
      d = parseISO(input);
      return isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  }
  return null;
}
function alignStart(d: Date, type: string) {
  switch (type) {
    case "hour":
      return startOfHour(d);
    case "day":
      return startOfDay(d);
    case "week":
      return startOfISOWeek(d);
    case "month":
      return startOfMonth(d);
    default:
      return startOfDay(d);
  }
}
function addOne(d: Date, type: string) {
  switch (type) {
    case "hour":
      return addHours(d, 1);
    case "day":
      return addDays(d, 1);
    case "week":
      return addWeeks(d, 1);
    case "month":
      return addMonths(d, 1);
    default:
      return addDays(d, 1);
  }
}
/** Exclude the partial current bucket */
function completedEnd(type: string, maybeEnd: Date | null) {
  const now = nowAsNashvilleLocal();
  const upto = maybeEnd && maybeEnd < now ? maybeEnd : now;
  return alignStart(upto, type);
}
const formatForInterval = (date: Date, type: string) => {
  switch (type) {
    case "hour":
      return format(date, "yyyy-MM-dd HH:00");
    case "day":
      return format(date, "yyyy-MM-dd");
    case "week":
      return format(startOfISOWeek(date), "yyyy-MM-dd") + " (week)";
    case "month":
      return format(date, "yyyy-MM");
    default:
      return format(date, "yyyy-MM-dd");
  }
};
// ------------------------------------

const FoamBlenders: React.FC = () => {
  // Fixed to foam blender_id = 5
  const blenderId = "5";

  // Match Parameters UX: quick range with optional Start/End that override it.
  const [rangeHrs, setRangeHrs] = useState<number>(4);
  const [start, setStart] = useState<Date | null>(null);
  const [end, setEnd] = useState<Date | null>(null);
  const anchoredEndRef = useRef<Date | null>(null); // anchor "now" when quick range is clicked

  // Grouping + units
  const [interval, setInterval] = useState<"hour" | "day" | "week" | "month">(
    "hour"
  );
  const [unit, setUnit] = useState<"kg" | "lb">("lb");

  const [loadingFlow, setLoadingFlow] = useState(false);
  const [rawData, setRawData] = useState<BlenderFlowRow[]>([]);

  // --- compute window like other pages ---
  const computeWindow = (): [number, number] => {
    if (start && end) {
      return [start.getTime(), end.getTime()];
    }
    const now = (anchoredEndRef.current ?? new Date()).getTime();
    const s = now - rangeHrs * 3600_000;
    return [s, now];
  };

  const convertKgToUnit = (kg: number | null | undefined) => {
    const v = Number(kg ?? 0);
    return unit === "kg" ? v : Number((v * 2.20462).toFixed(3));
  };
  const unitLabel = unit;

  const fetchFlow = async () => {
    setLoadingFlow(true);
    try {
      const [sMs, eMs] = computeWindow();
      // Respect "exclude partial current bucket"
      const effEnd = completedEnd(interval, new Date(eMs));

      const rows = await getBlenderFlow({
        startISO: nashvilleLocalToISO(new Date(sMs)),
        endISO: nashvilleLocalToISO(effEnd),
        groupBy: interval,
        blenderId,
      });

      setRawData(rows);
    } catch {
      setRawData([]);
    } finally {
      setLoadingFlow(false);
    }
  };

  useEffect(() => {
    fetchFlow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeHrs, start, end, interval, unit]);

  // Aggregate rows into full buckets
  const grouped = useMemo(() => {
    const [sMs, eMs] = computeWindow();
    const s = new Date(sMs);
    const cap = completedEnd(interval, new Date(eMs));
    const startAligned = alignStart(s, interval);
    if (!isBefore(startAligned, cap))
      return { bar: [] as GroupedBar[], line: [] as GroupedLine[] };

    const keys: number[] = [];
    for (
      let cur = new Date(startAligned);
      isBefore(cur, cap);
      cur = addOne(cur, interval)
    ) {
      keys.push(cur.getTime());
    }

    const map = new Map<
      number,
      { f1: number; f2: number; f3: number; c: number }
    >();
    for (const r of rawData) {
      const d = toValidDate(r.period as any);
      if (!d || !(d < cap)) continue;
      const k = alignStart(d, interval).getTime();
      const prev = map.get(k) || { f1: 0, f2: 0, f3: 0, c: 0 };
      prev.f1 += Number(r.delta_value1_kg || 0);
      prev.f2 += Number(r.delta_value2_kg || 0);
      prev.f3 += Number(r.delta_value3_kg || 0);
      prev.c += 1;
      map.set(k, prev);
    }

    const bar: GroupedBar[] = keys.map((k) => {
      const v = map.get(k) || { f1: 0, f2: 0, f3: 0, c: 0 };
      const base = {
        timestamp: k,
        label: formatForInterval(new Date(k), interval),
        flow1: convertKgToUnit(v.f1),
        flow2: convertKgToUnit(v.f2),
      } as GroupedBar;
      if (v.f3 > 0) base.flow3 = convertKgToUnit(v.f3);
      return base;
    });

    const line: GroupedLine[] = keys.map((k) => {
      const v = map.get(k) || { f1: 0, f2: 0, f3: 0, c: 0 };
      const count = v.c || 1;
      const base = {
        timestamp: k,
        label: formatForInterval(new Date(k), interval),
        flowRate1: convertKgToUnit(v.f1 / count),
        flowRate2: convertKgToUnit(v.f2 / count),
      } as GroupedLine;
      if (v.f3 > 0) base.flowRate3 = convertKgToUnit(v.f3 / count);
      return base;
    });

    return { bar, line };
  }, [rawData, interval, unit, rangeHrs, start, end]);

  // --- Handlers matching Parameters UX ---
  const handleQuickRangeClick = (hrs: number) => {
    anchoredEndRef.current = nowAsNashvilleLocal(); // anchor "now"
    setRangeHrs(hrs);
    // clear manual pickers so quick range is authoritative
    setStart(null);
    setEnd(null);
  };
  const clearRange = () => {
    setStart(null);
    setEnd(null);
    anchoredEndRef.current = nowAsNashvilleLocal();
    fetchFlow();
  };
  const handleIntervalChange = (
    _: any,
    next: "hour" | "day" | "week" | "month" | null
  ) => {
    if (next) setInterval(next);
  };

  const flowLabels = useMemo(
    () => ({ flow1: "Material 1", flow2: "Material 2", flow3: "Regrind" }),
    []
  );

  // Cumulative totals per material over the displayed period. Summed from the
  // bar buckets so the cards always match the chart.
  const materialTotals = useMemo(() => {
    const values: Record<string, number> = {
      [flowLabels.flow1]: 0,
      [flowLabels.flow2]: 0,
    };
    let regrind = 0;
    for (const b of grouped.bar) {
      values[flowLabels.flow1] += b.flow1 || 0;
      values[flowLabels.flow2] += b.flow2 || 0;
      regrind += b.flow3 || 0;
    }
    if (regrind > 0) values[flowLabels.flow3] = regrind;
    return [{ id: blenderId, label: "Foam Blender", values }];
  }, [grouped, flowLabels, blenderId]);

  const formatTotal = (v: number) =>
    `${v.toLocaleString(undefined, { maximumFractionDigits: 1 })} ${unitLabel}`;

  return (
    <Grid
      container
      spacing={2}
      padding={{ xs: 1, sm: 2 }}
      maxWidth="xl"
      margin="auto"
    >
      {/* Filters */}
      <Grid item xs={12}>
        <Card>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    label="Start"
                    value={start}
                    onChange={(v) => setStart(v)}
                    slotProps={{
                      textField: { fullWidth: true, size: "small" },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box display="flex" alignItems="center" gap={1}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateTimePicker
                      label="End"
                      value={end}
                      onChange={(v) => setEnd(v)}
                      slotProps={{
                        textField: { fullWidth: true, size: "small" },
                      }}
                    />
                  </LocalizationProvider>
                  <Tooltip title="Clear range">
                    <IconButton onClick={clearRange} size="small">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <ToggleButtonGroup
                  value={String(rangeHrs)}
                  exclusive
                  onChange={(_, v) => v && handleQuickRangeClick(Number(v))}
                  fullWidth
                >
                  {[1, 4, 12, 24, 720].map((h) => (
                    <ToggleButton key={h} value={String(h)}>
                      {h < 24 ? `${h}H` : `${h / 24}D`}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <Typography variant="caption" color="text.secondary">
                  If a date range is set, it overrides the quick range.
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <ToggleButtonGroup
                  value={unit}
                  exclusive
                  onChange={(_, u) => u && setUnit(u)}
                  fullWidth
                >
                  <ToggleButton value="kg">KG</ToggleButton>
                  <ToggleButton value="lb">LB</ToggleButton>
                </ToggleButtonGroup>
              </Grid>

              <Grid item xs={12} sm={6} md={1}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={fetchFlow}
                  disabled={loadingFlow}
                >
                  {loadingFlow ? "Loading…" : "Refresh"}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Cumulative totals per material over the displayed period */}
      <Grid item xs={12}>
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ mb: 1 }}
        >
          Totals for the selected period
        </Typography>
        <LoadingOverlay show={loadingFlow} minHeight={110}>
          <MaterialTotalsCards
            blenders={materialTotals}
            formatValue={formatTotal}
          />
        </LoadingOverlay>
      </Grid>

      {/* Usage (grouped totals) */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <LoadingOverlay show={loadingFlow} minHeight={340}>
              <BlenderFlowChart
                data={grouped.bar}
                unitLabel={unitLabel}
                flowLabels={flowLabels}
              />
            </LoadingOverlay>

            <Grid container justifyContent="center" sx={{ mt: 2 }}>
              <ToggleButtonGroup
                value={interval}
                exclusive
                onChange={handleIntervalChange}
              >
                <ToggleButton value="hour">HOUR</ToggleButton>
                <ToggleButton value="day">DAY</ToggleButton>
                <ToggleButton value="week">WEEK</ToggleButton>
                <ToggleButton value="month">MONTH</ToggleButton>
              </ToggleButtonGroup>
            </Grid>

            <Box mt={2} display="flex" justifyContent="center">
              <Button
                variant="outlined"
                onClick={() => {
                  const headers = [
                    "timestamp",
                    "flow1",
                    "flow2",
                    "flowRate1",
                    "flowRate2",
                  ];
                  const rows = grouped.bar.map((b, i) => {
                    const l = grouped.line[i];
                    const ts = formatForInterval(
                      new Date(b.timestamp),
                      interval
                    );
                    return [
                      ts,
                      b.flow1,
                      b.flow2,
                      l?.flowRate1 ?? 0,
                      l?.flowRate2 ?? 0,
                    ].join(",");
                  });
                  const csv = [headers.join(","), ...rows].join("\n");
                  const blob = new Blob([csv], {
                    type: "text/csv;charset=utf-8;",
                  });
                  const link = document.createElement("a");
                  link.href = URL.createObjectURL(blob);
                  link.setAttribute(
                    "download",
                    `foam_blender_${blenderId}_flow_${Date.now()}.csv`
                  );
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                EXPORT CSV
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Flow Rate (avg per bucket) */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <LoadingOverlay show={loadingFlow} minHeight={340}>
              <FlowRateChart
                data={grouped.line}
                unitLabel={unitLabel}
                flowLabels={flowLabels}
              />
            </LoadingOverlay>

            <Grid container justifyContent="center" sx={{ mt: 2 }}>
              <ToggleButtonGroup
                value={interval}
                exclusive
                onChange={handleIntervalChange}
              >
                <ToggleButton value="hour">HOUR</ToggleButton>
                <ToggleButton value="day">DAY</ToggleButton>
                <ToggleButton value="week">WEEK</ToggleButton>
                <ToggleButton value="month">MONTH</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default FoamBlenders;
